import { useCallback, useEffect, useState } from 'react';

export interface StorageEstimateInfo {
	usage: number | undefined;
	quota: number | undefined;
}

/**
 * Requests persistent storage so an installed reader's downloaded issues
 * aren't the first thing evicted under storage pressure. Best-effort only —
 * browsers grant this based on engagement heuristics, never guaranteed.
 * Call once the app is confirmed running installed/standalone.
 */
export function usePersistentStorage(enabled: boolean) {
	useEffect(() => {
		if (!enabled) {
			return;
		}
		if (!('storage' in navigator) || !('persist' in navigator.storage)) {
			return;
		}
		navigator.storage
			.persisted()
			.then(alreadyPersisted => {
				if (alreadyPersisted) {
					return undefined;
				}
				return navigator.storage.persist();
			})
			.then(granted => {
				if (granted !== undefined) {
					console.info(
						`Persistent storage ${granted ? 'granted' : 'denied'}`,
					);
				}
			})
			.catch(console.error);
	}, [enabled]);
}

/**
 * Surfaces navigator.storage.estimate() (usage/quota bytes) for
 * settings/downloads UI. Call refresh() after a download completes to get
 * an up-to-date number.
 *
 * No UI consumer yet (confirmed by grep, 2026-08-16) -- topic 11
 * (App-like PWA Features) owns wiring this into a real downloads/storage
 * view. Flagged rather than deleted since it's real, working, purpose-built
 * infrastructure waiting on that feature, not orphaned code.
 */
export function useStorageEstimate(): StorageEstimateInfo & {
	refresh: () => void;
} {
	const [estimate, setEstimate] = useState<StorageEstimateInfo>({
		usage: undefined,
		quota: undefined,
	});

	const refresh = useCallback(() => {
		if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
			return;
		}
		navigator.storage
			.estimate()
			.then(result => {
				setEstimate({ usage: result.usage, quota: result.quota });
			})
			.catch(console.error);
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return { ...estimate, refresh };
}
